import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Stepper,
  Step,
  StepLabel,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  Box,
} from '@mui/material';
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { HiVideoCamera } from "react-icons/hi2";
import { BiSolidCloudUpload } from "react-icons/bi";
import { IoCloseCircle } from "react-icons/io5";
import { CameraAlt, FolderOpen } from "@mui/icons-material";
import { X } from 'lucide-react';

// Constants
const STEPS = ["Review Info", "Submit Review"];
const STEP_COLOR = "#3686FF";
const API_ENDPOINT = "https://api.happiclips.com/api/save-review";

// Custom hooks
export const useUrlParams = () => {
  const location = useLocation();
  
  return React.useMemo(() => {
    try {
      const pathSegments = location.pathname.split("/");
      const encodedToken = pathSegments[pathSegments.length - 1];
      const decodedQuery = atob(encodedToken);
      const queryParams = new URLSearchParams(decodedQuery);
      
      return {
        id: queryParams.get("id"),
        eventId: queryParams.get("event_id"),
      };
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
      return { id: null, eventId: null };
    }
  }, [location.pathname]);
};

const useDeviceDetection = () => {
  return React.useMemo(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) return "Android";
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
    return "Unknown";
  }, []);
};

// Form validation
const validateForm = (formData) => {
  const { first_name, last_name, email, video } = formData;
  return first_name && last_name && email && video;
};

// Media recorder hook
const useMediaRecorder = (videoRef, onRecordingComplete) => {
  const [isRecording, setIsRecording] = useState(false);
  const [stream, setStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const recordedChunks = useRef([]);

  const startRecording = useCallback(async () => {
    recordedChunks.current = [];
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      const recorder = new MediaRecorder(mediaStream);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunks.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const videoBlob = new Blob(recordedChunks.current, { type: "video/webm" });
        const videoUrl = URL.createObjectURL(videoBlob);
        const videoFile = new File([videoBlob], "recorded_video.webm", { type: "video/webm" });
        
        onRecordingComplete(videoFile, videoUrl);
        setIsRecording(false);
        setStream(null);
        
        // Update video element
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.removeAttribute("src");
          videoRef.current.src = videoUrl;
          videoRef.current.play();
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setStream(mediaStream);
      setIsRecording(true);

      // Set up live preview
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error("Error accessing webcam:", error);
      toast.error("Erreur d'accès à la caméra. Vérifiez les permissions.");
    }
  }, [videoRef, onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, [mediaRecorder, stream]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    stream,
  };
};

// Loading component
const LoadingOverlay = () => (
  <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center backdrop-blur-sm z-50">
    <div className="flex items-center">
      <div role="status">
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C0 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Chargement...</span>
      </div>
      <span className="ml-2 text-white font-semibold">Veuillez patienter...</span>
    </div>
  </div>
);

// Success screen component
const SuccessScreen = () => (
  <div className="flex items-center h-[98vh]">
    <div className="bg-white max-w-4xl mx-auto h-100 p-8 md:p-16 flex flex-col justify-center items-center gap-6 md:gap-10">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
          Merci pour votre participation ! 🙌
        </h1>
        <p className="text-gray-600">Votre témoignage a été soumis avec succès.</p>
      </div>
    </div>
  </div>
);

// Form input component
const FormInput = ({ label, type = "text", placeholder, value, name, onChange, required = false }) => (
  <div className="flex flex-col gap-2 w-full">
    <span className="text-[14px] font-semibold text-[#131415] text-left">
      {label} {required && <span className="text-red-500">*</span>}
    </span>
    <input
      type={type}
      placeholder={placeholder}
      className="border border-[#c3cbd7] h-[45px] py-[12px] rounded-[10px] px-3 w-full focus:border-[#3686FF] focus:outline-none transition-colors"
      value={value}
      name={name}
      onChange={onChange}
      required={required}
    />
  </div>
);

// Main component
const VideoTestimonials = ({ onClose }) => {
  // Custom hooks
  const { id, eventId } = useUrlParams();
  const device = useDeviceDetection();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    event_id: eventId,
    guest_id: id,
    first_name: "",
    last_name: "",
    email: "",
    video: null,
  });

  // Refs
  const videoRef = useRef(null);

  // Media recorder
  const handleRecordingComplete = useCallback((videoFile, url) => {
    setFormData(prev => ({ ...prev, video: videoFile }));
    setVideoUrl(url);
    setSelectedVideo(null);
  }, []);

  const { isRecording, startRecording, stopRecording, stream } = useMediaRecorder(
    videoRef,
    handleRecordingComplete
  );

  // Event handlers
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleVideoFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setSelectedVideo(url);
      setFormData(prev => ({ ...prev, video: file }));
      setVideoUrl(null);
      event.target.value = null;
    }
    setOpenMenu(false);
  }, []);

  const handleClearVideo = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
    
    setSelectedVideo(null);
    setVideoUrl(null);
    setFormData(prev => ({ ...prev, video: null }));
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
  }, [isRecording, stopRecording]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm(formData)) {
      toast.error("Veuillez remplir tous les champs obligatoires !");
      return;
    }

    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) {
        form.append(key, value);
      }
    });

    try {
      setLoading(true);
      await axios.post(API_ENDPOINT, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setIsSubmitted(true);
      setFormData({
        event_id: eventId,
        guest_id: id,
        first_name: "",
        last_name: "",
        email: "",
        video: null,
      });
      setActiveStep(0);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
        "Erreur ! Lors de la soumission de l'avis. Veuillez réessayer.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData, eventId, id]);

  const handleNext = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
    
    if (activeStep === STEPS.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, handleSubmit, isRecording, stopRecording]);

  const handleBack = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
    setActiveStep(prev => prev - 1);
  }, [isRecording, stopRecording]);

  const handleOpenUploadOptions = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const handleStartRecording = useCallback(() => {
    setOpenMenu(false);
    setSelectedVideo(null);
    startRecording();
  }, [startRecording]);

  // Effects
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      if (isRecording && stream) {
        video.srcObject = stream;
        video.removeAttribute("src");
        video.play();
      } else if (selectedVideo) {
        video.srcObject = null;
        video.src = selectedVideo;
        video.load();
      } else if (videoUrl && !isRecording) {
        video.srcObject = null;
        video.src = videoUrl;
        video.load();
      } else {
        video.srcObject = null;
        video.removeAttribute("src");
        video.load();
      }
    }
  }, [isRecording, stream, selectedVideo, videoUrl]);

  // Step content renderers
  const renderFormStep = () => (
    <div className="mt-4 rounded-3xl bg-white">
      <div className="border-b p-4 font-semibold text-lg">
        Veuillez entrer vos détails.
      </div>
      <div className="flex flex-col justify-between min-h-[70vh]">
        <Box>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
            <FormInput
              label="Prénom"
              placeholder="Entrez votre prénom"
              value={formData.first_name}
              name="first_name"
              onChange={handleInputChange}
              required
            />
            <FormInput
              label="Nom de famille"
              placeholder="Entrez votre nom de famille"
              value={formData.last_name}
              name="last_name"
              onChange={handleInputChange}
              required
            />
            <FormInput
              label="Email"
              type="email"
              placeholder="Entrez votre email"
              value={formData.email}
              name="email"
              onChange={handleInputChange}
              required
            />
          </div>
          
          {/* Welcome section */}
          <div className="bg-white p-6 md:p-8 rounded-lg max-w-xl mx-auto text-gray-800">
            <h2 className="text-3xl font-extrabold text-center text-blue-600 mb-5">
              Bienvenue sur Happiclips ! 🎥✨
            </h2>
            <p className="text-lg text-center text-gray-700 mb-4">
              Partagez un témoignage vidéo unique pour l'événement de vos proches.<br />
              Un mot doux, une anecdote, une déclaration… laissez libre cours à votre imagination !
            </p>
            <p className="text-center text-gray-600 font-semibold mb-6">
              ⏳ <span className="font-bold">Vous avez 1 minute</span> pour enregistrer votre message.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg shadow-inner mb-6">
              <p className="text-center font-semibold text-gray-800 mb-3">
                Identifiez-vous et choisissez votre mode de témoignage :
              </p>
              <div className="flex flex-col space-y-3">
                <div className="flex items-center bg-white px-4 py-3 rounded-lg shadow-md">
                  <span className="text-2xl mr-3">🎬</span>
                  <span className="text-gray-700 font-medium">
                    Enregistrez-vous directement via l'application.
                  </span>
                </div>
                <div className="flex items-center bg-white px-4 py-3 rounded-lg shadow-md">
                  <span className="text-2xl mr-3">📁</span>
                  <span className="text-gray-700 font-medium">
                    Téléchargez une vidéo déjà enregistrée.
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-center text-gray-700 font-medium">
              🙏 Merci pour votre contribution et{" "}
              <span className="text-blue-500 font-semibold">belle expérience</span>{" "}
              sur Happiclips !
            </p>
          </div>
        </Box>
        
        {/* Navigation buttons */}
        <div className="flex justify-end items-center w-full p-4 border-t">
          <button
            onClick={handleNext}
            className="flex justify-center items-center h-[42px] bg-[#3686FF] text-white px-4 rounded-lg gap-2 hover:bg-blue-600 transition-colors"
          >
            Suivante
            <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );

  const renderVideoStep = () => (
    <div className="mt-4 rounded-3xl bg-white">
      <div className="border-b p-4 font-semibold text-lg">
        Veuillez ajouter votre avis vidéo.
      </div>
      <div className="flex flex-col justify-between min-h-[70vh]">
        <div className="m-4 border-2 border-dashed p-4 rounded-xl flex flex-col justify-center items-center py-10 gap-2 relative">
          {/* Empty state */}
          {(!isRecording && !videoUrl && !selectedVideo) && (
            <>
              <div className="bg-[#F6F6F6] rounded-full p-6">
                <HiVideoCamera size={38} />
              </div>
              <div className="font-semibold text-xl text-[#3686FF]">
                Vidéo vide
              </div>
              <div className="text-normal text-[#676774]">
                Veuillez ajouter votre vidéo d'avis.
              </div>
            </>
          )}
          
          {/* Clear video button */}
          {(videoUrl || selectedVideo) && !isRecording && (
            <span className="absolute top-5 right-5">
              <IoCloseCircle
                size={25}
                className="cursor-pointer text-gray-500 hover:text-red-500 transition-colors"
                onClick={handleClearVideo}
              />
            </span>
          )}

          {/* Video player */}
          {(isRecording || videoUrl || selectedVideo) && (
            <div className="w-full max-w-[300px] h-[500px] border rounded-xl overflow-hidden mt-2">
              <video
                ref={videoRef}
                autoPlay={isRecording}
                muted={isRecording}
                controls={!isRecording}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Recording controls */}
          {isRecording ? (
            <div className="mt-4 flex gap-3 justify-center items-center flex-wrap">
              <button
                onClick={stopRecording}
                className="flex justify-center items-center h-[42px] bg-[#ED3939] text-white px-4 rounded-lg gap-2 hover:bg-red-600 transition-colors"
              >
                Arrêter l'enregistrement
              </button>
            </div>
          ) : (!videoUrl && !selectedVideo) && (
            <div className="flex gap-3 justify-center items-center mt-4 flex-wrap">
              <button
                onClick={handleOpenUploadOptions}
                className="flex justify-center items-center h-[42px] bg-[#ED3939] text-white px-4 rounded-lg gap-2 cursor-pointer hover:bg-red-600 transition-colors"
              >
                <BiSolidCloudUpload size={22} />
                Téléverser la vidéo
              </button>
            </div>
          )}

          {/* Upload options menu */}
          <Menu
            sx={{
              mt: -1,
              "& .MuiMenu-paper": {
                borderRadius: "10px",
                minWidth: "250px",
              },
            }}
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            {device !== "iOS" && (
              <MenuItem
                onClick={handleStartRecording}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="inherit">Prendre une vidéo</Typography>
                <ListItemIcon>
                  <CameraAlt fontSize="small" />
                </ListItemIcon>
              </MenuItem>
            )}
            <MenuItem sx={{ display: "flex", justifyContent: "space-between" }}>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoFileChange}
                className="hidden"
                id="video-upload"
              />
              <label
                htmlFor="video-upload"
                className="flex justify-between items-center w-full cursor-pointer"
              >
                <Typography variant="inherit">Choisir le fichier</Typography>
                <ListItemIcon>
                  <FolderOpen fontSize="small" />
                </ListItemIcon>
              </label>
            </MenuItem>
          </Menu>
        </div>
        
        {/* Navigation buttons */}
        <div className="flex justify-between items-center w-full p-4 border-t">
          <button
            onClick={handleBack}
            className="flex justify-center items-center h-[42px] border border-[#3686FF] text-[#3686FF] px-4 rounded-lg gap-2 hover:bg-blue-50 transition-colors"
          >
            <FaArrowLeft />
            Retour
          </button>
          <button
            onClick={handleNext}
            className="flex justify-center items-center h-[42px] bg-[#3686FF] text-white px-4 rounded-lg gap-2 hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || (!videoUrl && !selectedVideo)}
          >
            Soumettre
            <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderFormStep();
      case 1:
        return renderVideoStep();
      default:
        return null;
    }
  };

  // Custom step icon component
  const StepIcon = ({ index }) => {
    let stepClass = "";
    if (activeStep === index) {
      stepClass = `bg-white border-2 border-[${STEP_COLOR}] text-[${STEP_COLOR}]`;
    } else if (index < activeStep) {
      stepClass = `bg-[${STEP_COLOR}] text-white`;
    } else {
      stepClass = `bg-white text-black`;
    }

    return (
      <div
        className={`flex items-center justify-center w-10 h-10 rounded-full ${stepClass} text-md font-semibold`}
      >
        {index + 1}
      </div>
    );
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      
      <div className="bg-[#EDF4FF] p-2 md:p-5">

        {!isSubmitted ? (
          <>
            <div className="max-w-3xl mx-auto my-3">
                <div className='flex items-center justify-between'>
                    <Stepper activeStep={activeStep} className="flex-1">
                        {STEPS.map((_, index) => (
                        <Step key={index}>
                            <StepLabel StepIconComponent={() => <StepIcon index={index} />} />
                        </Step>
                        ))}
                    </Stepper>

                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                            >
                            <X className="w-6 h-6" />
                            </button>
                        )}
                </div>
            </div>
            <div className="max-w-3xl mx-auto">{renderStepContent()}</div>
            
          </>
        ) : (
          <SuccessScreen />
        )}
      </div>
    </>
  );
};

export default VideoTestimonials;